import Router from "next/router";
import { useEffect } from "react";
import { Store } from "redux";

import { logoutUser } from "shared/data/actions/user";
import {
  COOKIE_CUSTOMER_DEVICE,
  COOKIE_CUSTOMER_NAME_NAME,
  COOKIE_CUSTOMER_TOKEN_NAME,
} from "shared/data/cookies";
import { deleteCookie, getCookie } from "shared/data/cookiesOps";
import {
  CUSTOMER_LOGOUT_STORAGE_KEY,
  clearCustomerSession,
  notifyOtherTabsAboutLogout,
} from "shared/data/customerTokens";

import {
  COOKIE_CUSTOMER_SESSION_INVALIDATED_NAME,
  createMandatoryLoginUrl,
  hasAuthenticatedCustomerSession,
  isPublicMandatoryLoginRoute,
} from "../lib/mandatoryLogin";

type UseMandatoryLoginSessionProps = {
  isMandatoryLoginRequired: boolean;
  language?: string | string[];
  reduxStore: Store;
};

const useMandatoryLoginSession = ({
  isMandatoryLoginRequired,
  language,
  reduxStore,
}: UseMandatoryLoginSessionProps): void => {
  useEffect(() => {
    let redirecting = false;

    const redirectAfterLogout = (
      redirectAfterLogin: string,
      notifyOtherTabs = false
    ) => {
      if (redirecting) {
        return;
      }

      redirecting = true;
      clearCustomerSession();
      if (notifyOtherTabs) {
        notifyOtherTabsAboutLogout();
      }
      reduxStore.dispatch(logoutUser());

      if (isMandatoryLoginRequired) {
        window.location.replace(
          createMandatoryLoginUrl(redirectAfterLogin, language)
        );
        return;
      }

      const resolvedLanguage = Array.isArray(language) ? language[0] : language;
      window.location.replace(
        resolvedLanguage
          ? `/?lang=${encodeURIComponent(resolvedLanguage)}`
          : "/"
      );
    };

    const enforceMandatoryLogin = (url: string) => {
      if (
        !isMandatoryLoginRequired ||
        isPublicMandatoryLoginRoute(url) ||
        hasAuthenticatedCustomerSession({
          customerToken: getCookie(COOKIE_CUSTOMER_TOKEN_NAME),
          customerName: getCookie(COOKIE_CUSTOMER_NAME_NAME),
          customerDevice: getCookie(COOKIE_CUSTOMER_DEVICE),
        })
      ) {
        return;
      }

      redirectAfterLogout(url, true);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === CUSTOMER_LOGOUT_STORAGE_KEY && event.newValue) {
        redirectAfterLogout(
          `${window.location.pathname}${window.location.search}${window.location.hash}`
        );
      }
    };

    const handleFocus = () => {
      enforceMandatoryLogin(
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    const handleServerSessionInvalidation = () => {
      if (getCookie(COOKIE_CUSTOMER_SESSION_INVALIDATED_NAME) !== "true") {
        return;
      }

      deleteCookie(COOKIE_CUSTOMER_SESSION_INVALIDATED_NAME);
      clearCustomerSession();
      reduxStore.dispatch(logoutUser());
      notifyOtherTabsAboutLogout();
    };

    Router.events.on("routeChangeStart", enforceMandatoryLogin);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    handleServerSessionInvalidation();
    handleFocus();

    return () => {
      Router.events.off("routeChangeStart", enforceMandatoryLogin);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isMandatoryLoginRequired, language, reduxStore]);
};

export default useMandatoryLoginSession;
